'use client';

import { useEffect, useRef, useState } from 'react';
import {
  useFeatureFlagEnabled,
  useHaveFeatureFlagsLoaded,
} from '@frameio/feature-flags';
import { useAccountPermissions } from '@frameio/permissions/hooks/useAccountPermissions';
import { useActiveAccountId, useFirstLoginContext } from '../../contexts';
import { useTrackUnsafe } from '../../segment/hooks';
import {
  useOnboardingTooltipState,
  useSyncTooltipAudienceContext,
  useSyncTooltipFeatureFlags,
} from '../hooks';
import { tooltipStore } from '../stores/tooltipStore';
import { OnboardingTooltipContainer } from './OnboardingTooltip/OnboardingTooltipContainer';

export const Onboarding = () => {
  const haveFlagsLoaded = useHaveFeatureFlagsLoaded();
  const isTooltipsFeatureFlagEnabled = useFeatureFlagEnabled(
    'v4.onboardingTooltips'
  );
  const { isFirstLogin, isFirstLoginLoading } = useFirstLoginContext();
  const { tooltipsEnabled, hasAnyTargetedFeatureEnabled } =
    useOnboardingTooltipState();
  const track = useTrackUnsafe();

  useSyncTooltipFeatureFlags();
  useSyncTooltipAudienceContext();
  const haveTooltipsEnrolledRef = useRef(false);
  const [isTooltipContainerReady, setIsTooltipContainerReady] = useState(false);

  const activeAccountId = useActiveAccountId();
  const isCurrentUserAccountOwner = useAccountPermissions({
    accountId: activeAccountId ?? '',
    check: 'canManageAll',
  });

  useEffect(() => {
    if (isFirstLoginLoading || !haveFlagsLoaded) {
      return undefined;
    }

    if (isFirstLogin) {
      setIsTooltipContainerReady(true);

      // Account-owner-only enrollment
      if (activeAccountId && isCurrentUserAccountOwner) {
        // Tooltips activation — ensure we only enroll once per session
        if (isTooltipsFeatureFlagEnabled) {
          if (!haveTooltipsEnrolledRef.current) {
            // Reset tooltip state for new users to prevent state leakage across accounts/sessions
            tooltipStore.resetOnboardingState();
            tooltipStore.enableTooltips();
            track('onboarding-tooltips-enrolled');
            haveTooltipsEnrolledRef.current = true;
          }
        }
      }
    } else {
      setIsTooltipContainerReady(true);
    }

    return undefined;
  }, [
    isFirstLogin,
    isFirstLoginLoading,
    haveFlagsLoaded,
    isTooltipsFeatureFlagEnabled,
    activeAccountId,
    isCurrentUserAccountOwner,
    track,
  ]);

  if (!haveFlagsLoaded) {
    return null;
  }

  const canRenderTooltips =
    (isTooltipsFeatureFlagEnabled && tooltipsEnabled) ||
    hasAnyTargetedFeatureEnabled;
  if (!canRenderTooltips || !isTooltipContainerReady) {
    return null;
  }

  return <OnboardingTooltipContainer />;
};
